import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Building, Camera, ChevronDown, ChevronRight, Edit, Plus, Trash2, Upload, X } from 'lucide-react';
import { API_BASE_URL } from '../config';
import useImageDrop from '../utils/useImageDrop';

const API = `${API_BASE_URL}/api`;

const TITLES = ['Commanding Officer', 'Deputy Commander', 'Director', 'Employee', 'Mayor', 'Counsellor', 'Decision Maker', 'Influencer', 'Staff member', 'Specialist', 'First Responder', 'Crew member', 'None', 'Other'];
const FACILITIES = ['Police Station', 'Fire Hall', 'EMS Station', 'Hospital', 'School', 'Sports Complex', 'Community Hall', 'City Hall', 'Evacuation Center', 'Operations Center', 'Command Post', 'Mobile Unit', 'Other'];
const emptyOrg = { name: '', description: '', organization_image: null };
const emptyPerson = { first_name: '', last_name: '', physical_location: '', phone: '', phone_extension: '', email: '', title: '', facility: '' };

const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => resolve(e.target.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

/**
 * OrganizationImageEditor — inline image editor used in the org create/edit form.
 * Supports click-to-browse, drag-and-drop and camera capture. Mirrors Step 8.
 */
const OrganizationImageEditor = ({ value, onChange, inputId }) => {
  const { dropProps, isDragging } = useImageDrop({
    onFile: async (file) => {
      try { onChange(await readFileAsDataURL(file)); } catch (e) { console.error(e); }
    }
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { onChange(await readFileAsDataURL(file)); } catch (err) { console.error(err); }
    e.target.value = '';
  };

  const handleCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      onChange(canvas.toDataURL('image/jpeg'));
      stream.getTracks().forEach((t) => t.stop());
    } catch (err) {
      alert('Camera unavailable: ' + err.message);
    }
  };

  return (
    <div className="flex items-center gap-4 p-3 bg-gray-800/60 rounded-lg border border-gray-700">
      <div
        {...dropProps}
        onClick={() => document.getElementById(inputId)?.click()}
        title="Click to browse or drop an image here"
        data-testid="org-image-dropzone"
        className={`w-20 h-20 rounded-lg bg-gray-700 border-2 border-dashed flex items-center justify-center overflow-hidden cursor-pointer transition-colors ${
          isDragging ? 'border-orange-500 bg-orange-500/10 ring-2 ring-orange-500' : 'border-gray-600 hover:border-orange-500/50'
        }`}
      >
        {value ? (
          <img src={value} alt="Organization" className="w-full h-full object-cover" />
        ) : (
          <div className="text-center px-1">
            <Camera className="h-5 w-5 text-gray-400 mx-auto mb-1" />
            <span className="text-[10px] text-gray-400">{isDragging ? 'Drop' : 'Drop here'}</span>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <input type="file" accept="image/*" onChange={handleFile} className="hidden" id={inputId} data-testid="org-image-file-input" />
        <div className="flex gap-2 flex-wrap">
          <button type="button" onClick={() => document.getElementById(inputId)?.click()} className="text-xs px-2 py-1 rounded border border-orange-500/50 text-orange-400 hover:bg-orange-500/10 flex items-center gap-1" data-testid="org-image-upload-btn">
            <Upload className="h-3 w-3" /> Upload
          </button>
          <button type="button" onClick={handleCamera} className="text-xs px-2 py-1 rounded border border-orange-500/50 text-orange-400 hover:bg-orange-500/10 flex items-center gap-1">
            <Camera className="h-3 w-3" /> Camera
          </button>
          {value && (
            <button type="button" onClick={() => onChange(null)} className="text-xs px-2 py-1 rounded border border-red-500/50 text-red-400 hover:bg-red-500/10 flex items-center gap-1" data-testid="org-image-remove-btn">
              <X className="h-3 w-3" /> Remove
            </button>
          )}
        </div>
        <p className="text-[11px] text-gray-500">Upload, capture, or drag &amp; drop an image.</p>
      </div>
    </div>
  );
};

/**
 * OrganizationsInlineManager — mirrors Step 8 Organizations for a given exercise,
 * writing back to the same `exercise.organizations` field on `/api/exercise-builder/{id}`.
 * Supports inline image edit (upload / camera / drag-and-drop) on create + edit.
 */
const OrganizationsInlineManager = ({ exerciseId }) => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [editingOrgId, setEditingOrgId] = useState(null);
  const [orgDraft, setOrgDraft] = useState(emptyOrg);
  const [personDrafts, setPersonDrafts] = useState({});
  const quickImgInputs = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/exercise-builder/${exerciseId}`);
        setOrgs(Array.isArray(r.data?.organizations) ? r.data.organizations : []);
      } finally {
        setLoading(false);
      }
    })();
  }, [exerciseId]);

  const persistOrgs = async (next) => {
    setOrgs(next);
    try {
      await axios.put(`${API}/exercise-builder/${exerciseId}`, { organizations: next });
    } catch (e) {
      console.error('Save failed:', e);
      alert('Save failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const toggle = (id) => setExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const startAddOrg = () => { setEditingOrgId('new'); setOrgDraft(emptyOrg); };
  const startEditOrg = (org) => { setEditingOrgId(org.id); setOrgDraft({ name: org.name || '', description: org.description || '', organization_image: org.organization_image || null }); };
  const cancelOrgEdit = () => { setEditingOrgId(null); setOrgDraft(emptyOrg); };
  const saveOrg = () => {
    if (!orgDraft.name.trim()) return;
    let next;
    if (editingOrgId === 'new') {
      next = [...orgs, { id: Date.now(), name: orgDraft.name.trim(), description: orgDraft.description.trim(), organization_image: orgDraft.organization_image || null, persons: [] }];
    } else {
      next = orgs.map((o) => (o.id === editingOrgId ? { ...o, name: orgDraft.name.trim(), description: orgDraft.description.trim(), organization_image: orgDraft.organization_image || null } : o));
    }
    persistOrgs(next);
    cancelOrgEdit();
  };
  const deleteOrg = (id) => { if (window.confirm('Delete this organization and its persons?')) persistOrgs(orgs.filter((o) => o.id !== id)); };

  // Quick image swap directly from an org card (no full edit needed)
  const updateOrgImage = (id, image) => persistOrgs(orgs.map((o) => (o.id === id ? { ...o, organization_image: image } : o)));
  const handleQuickImageFile = async (id, file) => {
    if (!file) return;
    try { updateOrgImage(id, await readFileAsDataURL(file)); } catch (e) { console.error(e); }
  };

  const getPersonState = (orgId) => personDrafts[orgId] || { form: emptyPerson, editingId: null };
  const setPersonState = (orgId, patch) => setPersonDrafts((p) => ({ ...p, [orgId]: { ...getPersonState(orgId), ...patch } }));
  const setPersonForm = (orgId, patch) => setPersonState(orgId, { form: { ...getPersonState(orgId).form, ...patch } });
  const resetPerson = (orgId) => setPersonState(orgId, { form: emptyPerson, editingId: null });
  const savePerson = (orgId) => {
    const st = getPersonState(orgId);
    if (!st.form.first_name.trim() || !st.form.last_name.trim()) return;
    const next = orgs.map((o) => {
      if (o.id !== orgId) return o;
      const persons = Array.isArray(o.persons) ? o.persons : [];
      if (st.editingId) return { ...o, persons: persons.map((p) => (p.id === st.editingId ? { ...st.form, id: p.id } : p)) };
      return { ...o, persons: [...persons, { ...st.form, id: Date.now() }] };
    });
    persistOrgs(next);
    resetPerson(orgId);
  };
  const editPerson = (orgId, person) => { setPersonState(orgId, { form: { ...emptyPerson, ...person }, editingId: person.id }); setExpanded((p) => new Set(p).add(orgId)); };
  const deletePerson = (orgId, personId) => {
    const next = orgs.map((o) => (o.id === orgId ? { ...o, persons: (o.persons || []).filter((p) => p.id !== personId) } : o));
    persistOrgs(next);
  };

  if (loading) return <div className="text-center py-8 text-gray-400">Loading organizations…</div>;

  return (
    <div className="space-y-4" data-testid="orgs-inline-manager">
      <div className="flex justify-between items-center">
        <div className="text-xs text-gray-400">
          Manages the same organizations as <a href={`#builder?exercise=${exerciseId}&step=8`} className="text-blue-400 hover:text-blue-300 underline">Step 8</a>. Images sync both ways — edit or drag &amp; drop below.
        </div>
        <button onClick={startAddOrg} className="bg-orange-500 hover:bg-orange-600 text-white text-sm px-3 py-1.5 rounded flex items-center gap-1" data-testid="orgs-add-btn">
          <Plus className="h-4 w-4" /> Add Organization
        </button>
      </div>

      {editingOrgId === 'new' && (
        <div className="bg-gray-700/60 rounded-lg p-4 border border-orange-500/40 space-y-3">
          <OrganizationImageEditor
            value={orgDraft.organization_image}
            onChange={(img) => setOrgDraft({ ...orgDraft, organization_image: img })}
            inputId="org-new-image-input"
          />
          <input value={orgDraft.name} onChange={(e) => setOrgDraft({ ...orgDraft, name: e.target.value })} placeholder="Organization name *" className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded" data-testid="org-new-name" />
          <textarea value={orgDraft.description} onChange={(e) => setOrgDraft({ ...orgDraft, description: e.target.value })} placeholder="Description" rows={2} className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded" />
          <div className="flex gap-2">
            <button onClick={saveOrg} className="bg-orange-500 hover:bg-orange-600 text-white text-sm px-3 py-1.5 rounded" data-testid="org-new-save-btn">Save</button>
            <button onClick={cancelOrgEdit} className="text-gray-400 hover:text-white text-sm px-3 py-1.5">Cancel</button>
          </div>
        </div>
      )}

      {orgs.length === 0 && editingOrgId !== 'new' && (
        <div className="text-center py-8 text-gray-400">No organizations yet. Click <b>Add Organization</b> above.</div>
      )}

      {orgs.map((org) => {
        const isExpanded = expanded.has(org.id);
        const persons = Array.isArray(org.persons) ? org.persons : [];
        const ps = getPersonState(org.id);
        return (
          <div key={org.id} className="bg-gray-700/60 rounded-lg p-4 border border-gray-600">
            {editingOrgId === org.id ? (
              <div className="space-y-3">
                <OrganizationImageEditor
                  value={orgDraft.organization_image}
                  onChange={(img) => setOrgDraft({ ...orgDraft, organization_image: img })}
                  inputId={`org-edit-image-input-${org.id}`}
                />
                <input value={orgDraft.name} onChange={(e) => setOrgDraft({ ...orgDraft, name: e.target.value })} className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded" />
                <textarea value={orgDraft.description} onChange={(e) => setOrgDraft({ ...orgDraft, description: e.target.value })} rows={2} className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded" />
                <div className="flex gap-2">
                  <button onClick={saveOrg} className="bg-orange-500 hover:bg-orange-600 text-white text-sm px-3 py-1.5 rounded">Save</button>
                  <button onClick={cancelOrgEdit} className="text-gray-400 hover:text-white text-sm px-3 py-1.5">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <button onClick={() => toggle(org.id)} className="mt-1 text-gray-400 hover:text-orange-400" data-testid={`org-toggle-${org.id}`}>
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                <OrgQuickImage
                  org={org}
                  onFile={(f) => handleQuickImageFile(org.id, f)}
                  onRemove={() => updateOrgImage(org.id, null)}
                  inputRef={(el) => (quickImgInputs.current[org.id] = el)}
                />
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-semibold">{org.name} {persons.length > 0 && <span className="ml-2 text-xs text-gray-400">({persons.length} {persons.length === 1 ? 'person' : 'persons'})</span>}</h4>
                  {org.description && <p className="text-gray-300 text-sm">{org.description}</p>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEditOrg(org)} className="p-2 text-blue-400 hover:text-blue-300" title="Edit organization" data-testid={`org-edit-btn-${org.id}`}><Edit className="h-4 w-4" /></button>
                  <button onClick={() => deleteOrg(org.id)} className="p-2 text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            )}

            {isExpanded && editingOrgId !== org.id && (
              <div className="mt-4 pt-3 border-t border-gray-600 space-y-3">
                {persons.map((p) => (
                  <div key={p.id} className="bg-gray-800 rounded p-3 flex justify-between items-start text-sm">
                    <div className="flex-1">
                      <div className="text-white font-semibold">{p.last_name}, {p.first_name}
                        {p.title && <span className="ml-2 text-xs px-2 py-0.5 rounded bg-orange-500/20 text-orange-300">{p.title}</span>}
                        {p.facility && <span className="ml-2 text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-300">{p.facility}</span>}
                      </div>
                      <div className="text-gray-400 text-xs mt-1">
                        {p.physical_location && <span>📍 {p.physical_location} </span>}
                        {p.phone && <span>· 📞 {p.phone}{p.phone_extension ? ` ext. ${p.phone_extension}` : ''} </span>}
                        {p.email && <span>· ✉ {p.email}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => editPerson(org.id, p)} className="p-1 text-blue-400 hover:text-blue-300"><Edit className="h-3 w-3" /></button>
                      <button onClick={() => deletePerson(org.id, p.id)} className="p-1 text-red-400 hover:text-red-300"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </div>
                ))}
                <div className="bg-gray-800 rounded-lg p-3 space-y-2">
                  <h5 className="text-sm font-semibold text-orange-400">{ps.editingId ? 'Edit Person' : 'Add Person'}</h5>
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Last Name *" value={ps.form.last_name} onChange={(e) => setPersonForm(org.id, { last_name: e.target.value })} className="px-2 py-1.5 bg-gray-700 border border-gray-600 text-white rounded text-sm" />
                    <input placeholder="First Name *" value={ps.form.first_name} onChange={(e) => setPersonForm(org.id, { first_name: e.target.value })} className="px-2 py-1.5 bg-gray-700 border border-gray-600 text-white rounded text-sm" />
                    <input placeholder="Physical Location" value={ps.form.physical_location} onChange={(e) => setPersonForm(org.id, { physical_location: e.target.value })} className="col-span-2 px-2 py-1.5 bg-gray-700 border border-gray-600 text-white rounded text-sm" />
                    <input placeholder="Phone" value={ps.form.phone} onChange={(e) => setPersonForm(org.id, { phone: e.target.value })} className="px-2 py-1.5 bg-gray-700 border border-gray-600 text-white rounded text-sm" />
                    <input placeholder="Ext" value={ps.form.phone_extension} onChange={(e) => setPersonForm(org.id, { phone_extension: e.target.value })} className="px-2 py-1.5 bg-gray-700 border border-gray-600 text-white rounded text-sm" />
                    <input placeholder="Email" value={ps.form.email} onChange={(e) => setPersonForm(org.id, { email: e.target.value })} className="col-span-2 px-2 py-1.5 bg-gray-700 border border-gray-600 text-white rounded text-sm" />
                    <select value={ps.form.title} onChange={(e) => setPersonForm(org.id, { title: e.target.value })} className="px-2 py-1.5 bg-gray-700 border border-gray-600 text-white rounded text-sm">
                      <option value="">Title / Profession…</option>
                      {TITLES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                    <select value={ps.form.facility} onChange={(e) => setPersonForm(org.id, { facility: e.target.value })} className="px-2 py-1.5 bg-gray-700 border border-gray-600 text-white rounded text-sm">
                      <option value="">Facility…</option>
                      {FACILITIES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-2 justify-end">
                    {ps.editingId && <button onClick={() => resetPerson(org.id)} className="text-gray-400 hover:text-white text-xs px-2">Cancel</button>}
                    <button onClick={() => savePerson(org.id)} disabled={!ps.form.first_name.trim() || !ps.form.last_name.trim()} className="bg-orange-500 hover:bg-orange-600 text-white text-xs px-3 py-1 rounded disabled:opacity-50">{ps.editingId ? 'Save Changes' : 'Add Person'}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * OrgQuickImage — the thumbnail on the collapsed org card. Click or drag &
 * drop directly onto it to swap the logo without opening the full edit form.
 */
const OrgQuickImage = ({ org, onFile, onRemove }) => {
  const inputId = `org-quick-image-${org.id}`;
  const { dropProps, isDragging } = useImageDrop({ onFile });
  return (
    <div className="relative group flex-shrink-0">
      <div
        {...dropProps}
        onClick={() => document.getElementById(inputId)?.click()}
        title="Click or drop an image to change"
        data-testid={`org-quick-image-${org.id}`}
        className={`w-14 h-14 rounded bg-gray-600 overflow-hidden cursor-pointer border-2 transition-colors ${
          isDragging ? 'border-orange-500 ring-2 ring-orange-500' : 'border-transparent hover:border-orange-500/60'
        }`}
      >
        {org.organization_image ? (
          <img src={org.organization_image} alt={org.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400"><Building className="h-7 w-7" /></div>
        )}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded pointer-events-none">
          <Camera className="h-4 w-4 text-white" />
        </div>
      </div>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
        data-testid={`org-quick-image-input-${org.id}`}
      />
      {org.organization_image && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          title="Remove image"
          className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </div>
  );
};

export default OrganizationsInlineManager;
